import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Download, Loader2, Pause, Play, Search, X } from "lucide-react";
import { AuditThumb } from "@/components/admin/audit/AuditThumb";

const TXT = {
    usage: { title: "Artikuj pa Usage / Cross-reference", action: "Plotëso nga ee-turbo", none: "Të gjithë artikujt kanë Usage." },
    images: { title: "Artikuj pa foto", action: "Merr fotot nga ee-turbo", none: "Të gjithë artikujt kanë foto." },
};

function JobBar({ job, onAction }) {
    if (!job?.running && !job?.finished_at) return null;
    const pct = job.target ? Math.round((job.done / job.target) * 100) : 0;
    return (
        <div className="border border-slate-200 bg-slate-50 p-3 font-mono text-xs space-y-2" data-testid="audit-job-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <span>
                    {job.running ? (job.paused ? <b className="text-amber-600">● PAUZË</b> : <b className="text-emerald-600">● DUKE PUNUAR</b>) : <b className="text-slate-600">● PËRFUNDOI</b>}
                    {" "}{job.done}/{job.target} · <span className="text-emerald-700">u plotësuan {job.fixed}</span> · nuk u gjetën {job.not_found} · <span className="text-red-600">dështuan {job.failed}</span>
                    {job.current ? ` · ${job.current}` : ""}
                </span>
                {job.running && (
                    <span className="flex gap-1">
                        {job.paused
                            ? <button onClick={() => onAction("resume")} className="btn-primary !py-1 !text-[10px]" data-testid="audit-job-resume"><Play className="w-3 h-3" /> Vazhdo</button>
                            : <button onClick={() => onAction("pause")} className="btn-outline !py-1 !text-[10px]" data-testid="audit-job-pause"><Pause className="w-3 h-3" /> Pauzë</button>}
                        <button onClick={() => onAction("cancel")} className="btn-outline !py-1 !text-[10px]" data-testid="audit-job-cancel"><X className="w-3 h-3" /> Anulo</button>
                    </span>
                )}
            </div>
            <div className="h-1.5 bg-slate-200"><div className="h-full bg-[hsl(var(--primary))] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            {job.results?.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-0.5" data-testid="audit-job-results">
                    {job.results.slice(0, 50).map((r) => (
                        <div key={r.id} className={r.status === "fixed" ? "text-emerald-700" : r.status === "failed" ? "text-red-600" : "text-slate-500"}>{r.code} — {r.info}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function MissingPanel({ kind, params, categoryId }) {
    const [list, setList] = useState(null);
    const [sel, setSel] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [job, setJob] = useState(null);
    const t = TXT[kind];

    const loadList = useCallback(() => api.get(`/admin/audit/list/${kind}`, { params: params() }).then(({ data }) => setList(data)).catch(() => {}), [kind, params]);
    const loadJob = useCallback(() => api.get("/admin/audit/job").then(({ data }) => setJob(data)).catch(() => {}), []);
    useEffect(() => { loadList(); setSel(new Set()); }, [loadList, categoryId]);
    useEffect(() => { loadJob(); }, [loadJob]);
    useEffect(() => {
        if (!job?.running) return;
        const id = setInterval(async () => {
            const { data } = await api.get("/admin/audit/job");
            setJob(data);
            if (!data.running) loadList();
        }, 2000);
        return () => clearInterval(id);
    }, [job?.running, loadList]);

    const scan = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/audit/scan/${kind}`, null, { params: params() });
            setList(data);
            setSel(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Skanimi dështoi");
        } finally {
            setBusy(false);
        }
    };
    const start = async () => {
        try {
            await api.post(`/admin/audit/fill/${kind}`, { ids: [...sel] });
            setJob((j) => ({ ...(j || {}), mode: kind, running: true, paused: false, done: 0, target: sel.size, fixed: 0, not_found: 0, failed: 0, results: [] }));
            setSel(new Set());
        } catch (e) {
            toast.error(e.response?.data?.detail || "Nuk u nis");
        }
    };
    const action = (a) => api.post(`/admin/audit/job/${a}`).then(({ data }) => setJob(data));

    const items = list?.items || [];
    const allOn = items.length > 0 && items.every((p) => sel.has(p.id));
    const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const toggleAll = () => setSel(allOn ? new Set() : new Set(items.slice(0, 2000).map((p) => p.id)));

    return (
        <section className="space-y-3" data-testid={`audit-missing-${kind}`}>
            <div className="flex flex-wrap items-center gap-3">
                <button onClick={scan} disabled={busy} className="btn-outline" data-testid={`audit-${kind}-scan`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {list?.scanned_at ? "Riskano" : "Skano"}
                </button>
                <button onClick={start} disabled={!sel.size || job?.running} className="btn-primary" data-testid={`audit-${kind}-fill`}>
                    <Download className="w-4 h-4" /> {t.action} ({sel.size})
                </button>
            </div>
            {(job?.mode === kind || (job?.running && !job?.mode)) && <JobBar job={job} onAction={action} />}
            <div className="text-sm" data-testid={`audit-${kind}-summary`}>
                <b>{t.title}:</b> {items.length}
                {list?.scanned_at && <span className="text-xs font-mono text-slate-500"> · skanuar {new Date(list.scanned_at).toLocaleString()} (lista ruhet)</span>}
            </div>
            {items.length > 0 && (
                <div className="border border-slate-200 bg-white max-h-[480px] overflow-y-auto" data-testid={`audit-${kind}-list`}>
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50">
                            <tr className="border-b border-slate-200 text-left">
                                <th className="p-2 w-8"><input type="checkbox" checked={allOn} onChange={toggleAll} data-testid={`audit-${kind}-check-all`} /></th>
                                <th className="p-2 w-12" />
                                <th className="p-2">Part №</th>
                                <th className="p-2">Kategoria</th>
                                <th className="p-2">Burimi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((p) => (
                                <tr key={p.id} className={`border-b border-slate-100 ${sel.has(p.id) ? "bg-emerald-50/60" : ""}`}>
                                    <td className="p-2"><input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} data-testid={`audit-${kind}-check-${p.id}`} /></td>
                                    <td className="p-2"><AuditThumb src={p.image_url} /></td>
                                    <td className="p-2"><a href={`/product/${p.id}`} target="_blank" rel="noreferrer" className="font-bold hover:underline">{p.part_number || "(pa kod)"}</a><div className="text-slate-500">{p.name}</div></td>
                                    <td className="p-2">{p.category}</td>
                                    <td className="p-2 font-mono text-slate-500">{p.from_ee ? "ee-turbo" : "manual"}{p.placeholder && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold" data-testid={`audit-${kind}-placeholder-${p.id}`}>NO PHOTO</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {list?.scanned_at && items.length === 0 && <div className="text-sm text-emerald-700" data-testid={`audit-${kind}-none`}>{t.none}</div>}
        </section>
    );
}
